import { useTranslation } from 'react-i18next';
import type { ISchedulerTask } from '@interfaces/IScheduler';
import { joinParts, promiseFloorLabel, promiseMeasureParts } from '@d-orca/shared/client/activities';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { ProgressMeter } from '@components/ui/ProgressMeter/ProgressMeter';
import type { IPromiseFacts } from '../WorkPlan/workPlanModel';
import styles from './cardList.module.css';

export interface IActivityLinesProps {
  task: ISchedulerTask;
  /** The card's formatted date range; empty when the task has none. */
  dateLabel: string;
  /** Floor, areas done and work time, from the site's catalog (`promiseFacts`). */
  facts?: IPromiseFacts;
}

/**
 * An activity card's two lines under its name, in the order a manager asks:
 * WHO, WHERE and WHEN — the trade's pill, then the floor and the dates — and
 * HOW FAR — a progress bar with its %, then the areas done of all and the
 * work time the IoT counted. How LATE it is lives in the status chip.
 */
export function ActivityLines({ task, dateLabel, facts }: IActivityLinesProps) {
  const { t } = useTranslation();
  const floor = facts ? promiseFloorLabel(facts) : null;
  const measures = joinParts(facts ? promiseMeasureParts(facts).map((part) => t(part.key, part.params)) : []);
  return (
    <>
      <div className={styles.line}>
        {task.profession && <ProfessionPill profession={task.profession} compact />}
        {/* A task always has its dates, so this line always has text. */}
        <span className={styles.lineText}>{joinParts([floor, dateLabel])}</span>
      </div>
      <div className={styles.line}>
        <ProgressMeter value={task.progressPercent} className={styles.meter} />
        <b className={styles.progress}>{task.progressPercent}%</b>
        {measures && <span className={styles.lineText}>{measures}</span>}
      </div>
    </>
  );
}
